import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import AdminPricing from "@/pages/AdminPricing";
import { getAdminEnquiries, patchEnquiryHandled, getAdminRegistrations, downloadRegistrationsCsv } from "@/lib/api";
import AdminRegistrations from "@/pages/AdminRegistrations";
import AdminTraffic from "@/pages/AdminTraffic";
import { Check, RotateCcw, LogOut } from "lucide-react";

const TYPE_LABELS = {
  booking: "Booking",
  general: "General",
  bridal: "Bridal",
  hair: "Hair assessment",
  waitlist: "Waitlist",
  journal: "Journal",
};

const TABS = [
  { key: "unhandled", label: "Needs action" },
  { key: "all", label: "All" },
  { key: "booking", label: "Bookings" },
  { key: "general", label: "General" },
  { key: "bridal", label: "Bridal" },
  { key: "hair", label: "Hair" },
  { key: "waitlist", label: "Waitlist" },
  { key: "journal", label: "Journal" },
];

const DETAIL_ROWS = [
  ["Consultation", "consultation_type"],
  ["Preferred date", "preferred_date"],
  ["Preferred time", "preferred_time"],
  ["Regarding", "service"],
  ["Practitioner", "practitioner"],
  ["Best time", "best_time"],
  ["Concern page", "concern"],
  ["Their concerns", "concerns"],
  ["First conversation", "conversation"],
  ["Where am I? stage", "stage"],
  ["Page", "page"],
  ["Date of the day", "wedding_date"],
  ["Preferred contact", "preferred_contact"],
  ["Message", "message"],
];

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
  } catch {
    return iso;
  }
};

const VIEWS = {
  traffic: ["Traffic & registrations", "Page views and visitors counted on this website, alongside Lumière list registrations and enquiries."],
  enquiries: ["Enquiries", "Every form submission from the website, newest first. Mark items handled as you action them."],
  registrations: ["Lumière list", "Everyone who has joined the list on /register, newest first. Download the CSV to import into Phorest."],
  pricing: ["Pricing", "The pricing page, editable. Save once when you're done — the website updates immediately."],
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const stored = JSON.parse(localStorage.getItem("lumiere_admin") || "null");
  const [view, setView] = useState("traffic");
  const [tab, setTab] = useState("unhandled");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem("lumiere_admin");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    if (!stored?.token) return;
    setLoading(true);
    try {
      const params = {};
      if (tab === "unhandled") params.handled = false;
      else if (tab !== "all") params.type = tab;
      setItems(await getAdminEnquiries(stored.token, params));
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [tab, stored?.token, logout]);

  useEffect(() => {
    if (!stored?.token) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [tab]);

  const toggleHandled = async (item) => {
    try {
      await patchEnquiryHandled(stored.token, item.id, !item.handled);
      load();
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
  };

  if (!stored?.token) return null;

  return (
    <div className="min-h-screen bg-warm-white" data-testid="admin-dashboard">
      <SEO title="Practice Dashboard | Lumière Aesthetics" description="Practice enquiries dashboard." />
      <header className="bg-deep-sea" data-testid="admin-header">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/images/lumiere-logo-light.png" alt="Lumière Aesthetics" className="h-5 w-auto" />
            <span className="text-mist text-xs tracking-[0.18em] uppercase hidden sm:inline">Practice dashboard</span>
          </div>
          <div className="flex items-center gap-5">
            <span className="text-mist text-sm hidden sm:inline" data-testid="admin-user-name">{stored.user?.name || stored.user?.email}</span>
            <button onClick={logout} data-testid="admin-logout-button" className="text-gold hover:text-warm-white text-xs tracking-[0.18em] uppercase font-medium inline-flex items-center gap-2 transition-colors">
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <h1 className="font-serif font-medium text-deep-sea text-3xl sm:text-4xl" data-testid="admin-view-title">{VIEWS[view][0]}</h1>
            <p className="text-ink/60 text-sm mt-2">{VIEWS[view][1]}</p>
          </div>
          <div className="flex gap-2" data-testid="admin-view-switch">
            {[["traffic", "Traffic"], ["enquiries", "Enquiries"], ["registrations", "Priority list"], ["pricing", "Pricing"]].map(([key, label]) => (
              <button key={key} onClick={() => setView(key)} data-testid={`admin-view-${key}`}
                className={`rounded-full border px-5 py-2 text-xs tracking-[0.12em] uppercase font-medium transition-colors duration-300 ${
                  view === key ? "bg-deep-sea text-cream border-deep-sea" : "border-deep-sea/20 text-deep-sea hover:border-gold"
                }`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {view === "traffic" ? (
          <AdminTraffic token={stored.token} onUnauthorized={logout} />
        ) : view === "pricing" ? (
          <AdminPricing token={stored.token} onUnauthorized={logout} />
        ) : view === "registrations" ? (
          <AdminRegistrations token={stored.token} onUnauthorized={logout} load={getAdminRegistrations} download={downloadRegistrationsCsv} />
        ) : (
          <>
        <div className="flex flex-wrap gap-2 mt-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`rounded-full border px-4 py-2 text-xs tracking-[0.12em] uppercase font-medium transition-colors duration-300 ${
                tab === t.key ? "bg-deep-sea text-cream border-deep-sea" : "border-deep-sea/20 text-deep-sea hover:border-gold"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-4" data-testid="admin-enquiry-list">
          {loading ? (
            <p className="text-soft-grey text-sm py-16 text-center">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-soft-grey text-sm py-16 text-center" data-testid="admin-empty">Nothing here — you're all caught up.</p>
          ) : (
            items.map((item) => (
              <div key={item.id} data-testid={`enquiry-card-${item.id}`}
                className={`bg-cream relative p-7 transition-opacity duration-300 ${item.handled ? "opacity-55" : ""}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <span className="eyebrow">{TYPE_LABELS[item.type] || item.type}</span>
                    <h2 className="font-serif font-semibold text-deep-sea text-xl mt-1.5">{item.name || "—"}</h2>
                    <p className="text-ink/60 text-sm mt-1">
                      {item.email}{item.phone ? ` · ${item.phone}` : ""}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-soft-grey text-xs">{formatDate(item.created_at)}</p>
                    <button onClick={() => toggleHandled(item)} data-testid={`mark-handled-${item.id}`}
                      className={`mt-3 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[11px] tracking-[0.14em] uppercase font-medium transition-colors duration-300 ${
                        item.handled ? "border-deep-sea/20 text-deep-sea hover:border-gold" : "border-gold text-gold hover:bg-deep-sea hover:text-cream hover:border-deep-sea"
                      }`}>
                      {item.handled ? <><RotateCcw className="w-3 h-3" strokeWidth={1.5} /> Reopen</> : <><Check className="w-3 h-3" strokeWidth={1.5} /> Mark handled</>}
                    </button>
                  </div>
                </div>
                <dl className="mt-4 space-y-1.5">
                  {DETAIL_ROWS.filter(([, f]) => item[f]).map(([label, f]) => (
                    <div key={f} className="flex gap-3 text-sm">
                      <dt className="text-soft-grey w-32 shrink-0">{label}</dt>
                      <dd className="text-ink/85">{item[f]}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))
          )}
        </div>
          </>
        )}

        <p className="text-soft-grey text-xs mt-10 text-center">
          Signed in as practice team · <Link to="/" className="link-sea">Back to the website</Link>
        </p>
      </main>
    </div>
  );
};

export default AdminDashboard;
